'use client';

import { useState, useCallback, useEffect } from 'react';
import {
  getAnalyticsConsent,
  setAnalyticsConsent,
  getConsentPreferences,
  setConsentPreferences,
  ALL_ACCEPTED,
  NONE_ACCEPTED,
  type ConsentValue,
  type ConsentPreferences,
} from '@/lib/consent';

export function useConsent() {
  const [consent, setConsent] = useState<ConsentValue | null | 'pending'>('pending');

  useEffect(() => {
    setConsent(getAnalyticsConsent());
  }, []);

  const acceptAll = useCallback(() => {
    setAnalyticsConsent('accepted');
    setConsentPreferences(ALL_ACCEPTED);
    setConsent('accepted');
  }, []);

  const savePreferences = useCallback((prefs: ConsentPreferences) => {
    setAnalyticsConsent(prefs.thirdParty ? 'accepted' : 'rejected');
    setConsentPreferences(prefs);
    setConsent(prefs.thirdParty ? 'accepted' : 'rejected');
  }, []);

  /**
   * First-layer "Reject all". Writes the same records as acceptAll so a
   * refusal is stored as an explicit decision rather than as the absence
   * of one, which is what makes it withdrawable and auditable later.
   */
  const rejectAll = useCallback(() => {
    setAnalyticsConsent('rejected');
    setConsentPreferences(NONE_ACCEPTED);
    setConsent('rejected');
  }, []);

  return {
    consent: consent === 'pending' ? 'pending' : consent,
    preferences: getConsentPreferences(),
    acceptAll,
    rejectAll,
    savePreferences,
    // Keep legacy API for any callers
    accept: () => { setAnalyticsConsent('accepted'); setConsentPreferences(ALL_ACCEPTED); setConsent('accepted'); },
    reject: () => { setAnalyticsConsent('rejected'); setConsent('rejected'); },
  };
}
